import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

/**
 * Progress tracker showing evidence strength
 * 
 * Displays:
 * - Filled/empty circles for visual progress
 * - Text summary (e.g., "3/5")
 * - Progress bar
 * - Completion status
 */
const ProgressTracker = ({ current, target = 5 }) => {
  const percentage = Math.round((current / target) * 100);
  const isComplete = current >= target;
  
  const color = isComplete ? '#22c55e' : '#3b82f6';
  const bgColor = isComplete ? '#dcfce7' : '#eff6ff';

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: bgColor,
        border: `1px solid ${isComplete ? '#86efac' : '#bfdbfe'}`
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
        Evidence Strength
      </Typography>

      {/* Circles */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {Array.from({ length: target }).map((_, index) => (
            index < current ? (
              <CircleIcon key={index} sx={{ fontSize: 20, color }} />
            ) : (
              <CircleOutlinedIcon key={index} sx={{ fontSize: 20, color: '#d1d5db' }} />
            )
          ))}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color }}>
          {current}/{target}
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 1,
          bgcolor: '#e5e7eb',
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 1
          }
        }}
      />

      {/* Status text */}
      <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
        {isComplete ? (
          <span style={{ color: '#22c55e', fontWeight: 600 }}>
            ✓ Ready to save
          </span>
        ) : (
          `${percentage}% complete - Keep going!`
        )}
      </Typography>
    </Box>
  );
};

export default ProgressTracker;
